import { isInvalidPurchaseOrderNumber, isInvalidFileName, isInvalidString } from "src/validation/stringValidation";
import { isInvalidISODate } from "src/helpers/dateHelper";
import { isInvalidFileSize, isInvalidFileType } from "src/helpers/fileHelper";
import { FormValidator } from "../formValidator";
import { ValidationError, ValidationErrors } from "src/errors";
import { UpdatePurchaseOrderInvoiceFormDataKey } from "src/types";

class UpdatePurchaseOrderInvoiceFormValidator extends FormValidator {
  private static validatePurchaseOrderNumber(value: unknown): ValidationError<"purchaseOrderId"> | undefined {
    return isInvalidPurchaseOrderNumber(value)
      ? new ValidationError("Enter a valid purchase order number (e.g., 'PO-2055')", "purchaseOrderId")
      : undefined;
  }

  private static validateInvoiceFiles(value: unknown): ValidationError<"invoiceFiles"> | undefined {
    if (!Array.isArray(value) || !value.length) {
      return new ValidationError("Upload a file", "invoiceFiles");
    }

    for (const arrayValue of value) {
      if (
        isInvalidFileType(arrayValue, [
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ])
      ) {
        return new ValidationError("File type is invalid", "invoiceFiles");
      }

      if (isInvalidFileSize(arrayValue)) {
        return new ValidationError("File size is invalid", "invoiceFiles");
      }

      if (isInvalidFileName(arrayValue.name)) {
        return new ValidationError("File name is invalid", "invoiceFiles");
      }
    }

    return undefined;
  }

  private static validateInvoiceDueDate(value: unknown): ValidationError<"invoiceDueDate"> | undefined {
    if (!value) {
      return new ValidationError("Enter a due date", "invoiceDueDate");
    }

    return isInvalidISODate(value)
      ? new ValidationError("Enter a valid date (example format: '01/01/2026')", "invoiceDueDate")
      : undefined;
  }

  private static validateAdditionalInfo(value: unknown): ValidationError<"additionalInfo"> | undefined {
    return value && isInvalidString(value, 0, 1200)
      ? new ValidationError("Additional information is too long or invalid", "additionalInfo")
      : undefined;
  }

  private static validateIsProforma(value: unknown): ValidationError<"isProforma"> | undefined {
    return typeof value !== "boolean"
      ? new ValidationError("Proforma value must be a boolean", "isProforma")
      : undefined;
  }
  private static validateInvoiceReferenceNumber(value: unknown): ValidationError<"invoiceReferenceNumber"> | undefined {
    return value && isInvalidString(value, 0, 200)
      ? new ValidationError("Invoice reference number is too long or invalid", "invoiceReferenceNumber")
      : undefined;
  }

  private static readonly validationStrategies: Record<
    UpdatePurchaseOrderInvoiceFormDataKey,
    (inputValue: unknown) => ValidationError<UpdatePurchaseOrderInvoiceFormDataKey> | undefined
  > = {
    purchaseOrderId: UpdatePurchaseOrderInvoiceFormValidator.validatePurchaseOrderNumber,
    invoiceFiles: UpdatePurchaseOrderInvoiceFormValidator.validateInvoiceFiles,
    invoiceDueDate: UpdatePurchaseOrderInvoiceFormValidator.validateInvoiceDueDate,
    additionalInfo: UpdatePurchaseOrderInvoiceFormValidator.validateAdditionalInfo,
    isProforma: UpdatePurchaseOrderInvoiceFormValidator.validateIsProforma,
    invoiceReferenceNumber: UpdatePurchaseOrderInvoiceFormValidator.validateInvoiceReferenceNumber
  };

  static validatePurchaseOrderFormData<T extends UpdatePurchaseOrderInvoiceFormDataKey>(
    formData: Partial<Record<T, unknown>>
  ): ValidationErrors<T> {
    return super.validateFormData(
      formData,
      UpdatePurchaseOrderInvoiceFormValidator.validationStrategies
    ) as ValidationErrors<T>;
  }
}

export { UpdatePurchaseOrderInvoiceFormValidator };
